<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'

import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

import ErrorText from '@/components/ErrorText.vue'
import TheFooter from '@/components/TheFooter.vue'

import { objectToString } from '@/utils/objectToString'
import { setRootCss } from '@/utils/root'

import BaseButton from '@/components/Base/BaseButton.vue'

import { clientApi } from '@/api/config'

import { useGameContest } from '@/store'

const props = defineProps<{
  id: string
}>()
const route = useRoute()
const { getMerchant } = useGameContest()

const hasNoResult = ref(false)
const isLoaded = ref(false)
const merchant = ref()

onMounted(async () => {
  await Promise.all([getMerchant(), fetchContestData()])
  isLoaded.value = true
})

const contest = ref(null)
const filteredContest = ref([])

const fetchContestData = async () => {
  try {
    const fetchContests = await clientApi.client.contestGames.readByPublicId(props.id)

    if (fetchContests.data.length === 0) {
      hasNoResult.value = true
      return
    }

    if (fetchContests.data.length === 1) {
      redirectTo(fetchContests.data[0].shortLink.shortLink)
      return
    }
    merchant.value = fetchContests.data[0].store.merchant
    setRootCss(merchant.value.color)
    const sortingContest = Object.values(fetchContests.data).sort(function (a, b) {
      return a.store.name.localeCompare(b.store.name)
    })

    contest.value = sortingContest
    filteredContest.value = sortingContest
  } catch (e) {
    hasNoResult.value = true
  }
}
const searchText = ref('')
const normalize = (text: string): string => {
  if (text !== null) {
    return text.normalize('NFD').replace(/\p{Diacritic}/gu, '')
  } else {
    return ''
  }
}
const search = useDebounceFn(() => {
  searchText.value = searchText.value.toLowerCase()
  filteredContest.value = [...contest.value].filter((el) => {
    const name =
      el.store.name != null &&
      normalize(el.store.name).toLowerCase().includes(normalize(searchText.value))

    const fieldsToInclude = [
      'streetNumber',
      'streetType',
      'streetName',
      'zipCode',
      'city',
      'streetNumberSupplement',
      'dependentLocality',
      'addressLine2',
      'addressLine3',
    ]

    if (el.store.storeAddress) {
      const includedFieldsAddress = Object.fromEntries(
        Object.entries(el.store.storeAddress).filter(([key]) => fieldsToInclude.includes(key)),
      )

      const address = normalize(objectToString(includedFieldsAddress))
        .toLowerCase()
        .includes(normalize(searchText.value))

      return name || address
    } else {
      return name
    }
  })
}, 250)

const redirectTo = (link: string) => {
  const origin = route.query?.origin

  if (origin) {
    link = `${link}?origin=${origin}`
  }

  window.location.href = `https://${link}`
}

const vFocus = {
  mounted: (el) => el.focus(),
}
</script>

<template>
  <div v-if="isLoaded" class="mx-auto max-w-4xl p-6">
    <ErrorText v-if="hasNoResult" :text="$t('errors.404')"> 🤕 </ErrorText>
    <template v-else>
      <div class="text-center">
        <img :src="merchant?.media?.logo" :alt="contest.name" class="mx-auto h-auto w-36" />
        <h2 class="mt-10 text-2xl font-medium">
          {{ $t('pages.storesList.subtitle') }}
        </h2>
      </div>
      <div v-if="contest.length > 4" class="my-4 flex items-center md:px-20">
        <div class="relative w-full">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon class="h-5 w-5" />
          </div>
          <input
            id="search-merchant"
            v-model="searchText"
            v-focus
            type="text"
            class="block w-full rounded-md border-2 border-gray-900 bg-white p-2.5 pl-10 text-gray-900 focus:outline-none focus:ring-transparent lg:text-sm"
            :placeholder="$t('pages.storesList.search')"
            required
            @input="search()"
          />
        </div>
      </div>
      <div v-if="filteredContest.length > 0" class="my-6 grid grid-cols-6 gap-4">
        <div
          v-for="store in filteredContest"
          :key="store.id"
          class="col-span-6 flex items-center justify-between rounded-md bg-gray-50 p-5 md:col-span-3"
        >
          <div class="w-full">
            <h3 class="flex-1 font-medium">
              {{ store.store.name }}
            </h3>
            <div v-if="store.store.storeAddress" class="text-xs text-gray-600">
              <p>
                {{
                  `${store.store.storeAddress.streetNumber ?? ''} ${
                    store.store.storeAddress.streetNumberSupplement ?? ''
                  } ${store.store.storeAddress.streetType ?? ''} ${store.store.storeAddress.streetName ?? ''} `
                }}
              </p>
              <p v-if="store.store.storeAddress.addressLine2">
                {{ store.store.storeAddress.addressLine2 }}
              </p>
              <p v-if="store.store.storeAddress.addressLine3">
                {{ store.store.storeAddress.addressLine3 }}
              </p>
              <p>
                {{
                  `${store.store.storeAddress.zipCode ?? ''} ${store.store.storeAddress.city ?? ''}`
                }}
              </p>
            </div>
          </div>
          <div>
            <base-button
              class="rounded-md text-base"
              size="sm"
              is-active
              is-animated
              @click="redirectTo(store.shortLink.shortLink)"
            >
              <span class="mx-3 my-0.5">{{ $t('pages.storesList.play') }}</span>
            </base-button>
          </div>
        </div>
      </div>
      <div v-else class="my-6 grid">
        <div class="flex items-center justify-between rounded-md bg-gray-50 p-5">
          <div class="mx-auto px-6 text-center lg:px-8">
            <p class="mt-2 text-4xl tracking-tight sm:text-5xl">🤷🏼‍♂</p>
            <p class="mt-2 text-xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
              {{ $t('pages.storesList.noResults.title') }}
            </p>
            <p class="mx-auto my-5 max-w-prose text-xs text-gray-500 sm:text-xl">
              {{ $t('pages.storesList.noResults.description') }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
  <TheFooter medium="store_selector" />
</template>
